import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/utils'

export type StatusAlertTone = 'loading' | 'error' | 'success' | 'warning' | 'info'

export interface StatusAlertProps {
  tone: StatusAlertTone
  title?: string
  children?: ReactNode
  className?: string
  icon?: LucideIcon
}

const TONE_STYLES: Record<
  StatusAlertTone,
  { wrap: string; icon: string; Icon: LucideIcon }
> = {
  loading: {
    wrap: 'border-primary/25 bg-primary/5 text-foreground',
    icon: 'text-primary',
    Icon: Loader2,
  },
  error: {
    wrap: 'border-destructive/30 bg-destructive/5 text-destructive',
    icon: 'text-destructive',
    Icon: AlertTriangle,
  },
  success: {
    wrap: 'border-success/30 bg-success/5 text-foreground',
    icon: 'text-success',
    Icon: CheckCircle2,
  },
  warning: {
    wrap: 'border-warning/35 bg-warning/10 text-foreground',
    icon: 'text-warning',
    Icon: AlertTriangle,
  },
  info: {
    wrap: 'border-info/30 bg-info/10 text-foreground',
    icon: 'text-info',
    Icon: Info,
  },
}

/**
 * Canonical inline feedback: loading / error / success / warning / info.
 */
export function StatusAlert({ tone, title, children, className, icon }: StatusAlertProps) {
  const cfg = TONE_STYLES[tone]
  const Icon = icon ?? cfg.Icon

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'loading' ? 'polite' : undefined}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm transition-colors duration-200',
        cfg.wrap,
        className,
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          cfg.icon,
          tone === 'loading' && 'animate-spin',
        )}
        aria-hidden
      />
      <div className="min-w-0 space-y-0.5">
        {title ? <p className="font-medium leading-snug">{title}</p> : null}
        {children ? (
          <div className={cn('leading-relaxed', title ? 'text-muted-foreground text-xs' : '')}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  )
}
